/*
 * Typed date entry (ADR 0029). The console stores and sends ISO days ("2026-11-02"); people read and type
 * them day first: Turkish "02.11.2026", English (en-GB) "02/11/2026". Native <input type="date"> follows the
 * browser's UI language instead of the page's, so an English browser showed mm/dd/yyyy on a Turkish screen.
 */

export type DateInputLang = "tr" | "en";

const SEP: Record<DateInputLang, string> = { tr: ".", en: "/" };

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Day first, any of . / - as separator, 1–2 digit day and month, 4 digit year. */
const DAY_FIRST = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/;
/**
 * Day first without separators ("02112026"): the numeric keypad of phones (inputmode="numeric") has no
 * "." or "/" key, so typing digits only must work too (ADR 0030).
 */
const COMPACT = /^(\d{2})(\d{2})(\d{4})$/;

const pad = (n: number) => String(n).padStart(2, "0");

/** True for a real calendar day (no 31.02., no month 13). */
function realDay(y: number, m: number, d: number): boolean {
  if (y < 1900 || y > 2999 || m < 1 || m > 12 || d < 1) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** "2026-11-02" → "02.11.2026" (tr) / "02/11/2026" (en); "" or an unreadable value → "". */
export function formatDateInput(iso: string | null | undefined, lang: DateInputLang): string {
  const m = ISO.exec(iso ?? "");
  if (!m) return "";
  return `${m[3]}${SEP[lang]}${m[2]}${SEP[lang]}${m[1]}`;
}

/**
 * Typed text → ISO day. Accepts day-first dates with . / or - (whatever the language, so a pasted
 * "02/11/2026" works on a Turkish screen), eight digits "02112026" and ISO "2026-11-02". Returns "" for empty input and null when
 * the text is not a real date.
 */
export function parseDateInput(text: string): string | null {
  const s = text.trim();
  if (!s) return "";
  let y: number, mo: number, d: number;
  const iso = ISO.exec(s);
  const df = iso ? null : (DAY_FIRST.exec(s) ?? COMPACT.exec(s));
  if (iso) [y, mo, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (df) [d, mo, y] = [Number(df[1]), Number(df[2]), Number(df[3])];
  else return null;
  return realDay(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
}

/** Format hint shown as placeholder and in the help text. */
export function datePattern(lang: DateInputLang): string {
  return lang === "tr" ? "gg.aa.yyyy" : "dd/mm/yyyy";
}

export type DateIssue = "invalid" | "required" | "tooEarly" | "tooLate";

/** Validate typed text against optional bounds (ISO strings compare correctly as text). */
export function checkDateInput(text: string, opts: { required?: boolean; min?: string; max?: string } = {}): { iso: string | null; issue: DateIssue | null } {
  const iso = parseDateInput(text);
  if (iso === null) return { iso: null, issue: "invalid" };
  if (iso === "") return { iso: "", issue: opts.required ? "required" : null };
  if (opts.min && iso < opts.min) return { iso, issue: "tooEarly" };
  if (opts.max && iso > opts.max) return { iso, issue: "tooLate" };
  return { iso, issue: null };
}
